'use client'
import Image from 'next/image'
import React, { useRef, useState } from 'react'
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'

const tabs = [
  {
    icons: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M8 0.5C3.37187 0.5 0.5 3.13438 0.5 7.375C0.5 9.5375 1.25 11.2781 2.59375 12.4688L0.5 14.5625L1.125 15.5C2.81875 15.5 4.38438 14.9375 5.64062 13.9875C6.36563 14.1594 7.15312 14.25 8 14.25C12.6281 14.25 15.5 11.6156 15.5 7.375C15.5 3.13438 12.6281 0.5 8 0.5ZM8 3C9.54375 3 10.5 3.95625 10.5 5.5C10.5 7.04375 9.54375 8 8 8C6.45625 8 5.5 7.04375 5.5 5.5C5.5 3.95625 6.45625 3 8 3ZM8 12.375C6.25 12.375 4.86562 11.9344 3.90937 11.1156C4.75938 9.925 6.17188 9.25 8 9.25C9.82812 9.25 11.2406 9.925 12.0906 11.1156C11.1344 11.9344 9.75 12.375 8 12.375Z"
          fill="black"
        />
      </svg>
    ),
    name: 'Create',
    description:
      'Instantly generate optimized forms with smart AI suggestions.',
    image: '/ai/menu1.svg'
  },
  {
    icons: (
      <svg width="12" height="18" viewBox="0 0 12 18" fill="none">
        <path
          d="M5.65997 0.875C2.55372 0.875 0.0349731 3.39375 0.0349731 6.5C0.0349731 8 0.631848 9.44063 1.69122 10.5L2.60685 11.4156C2.95997 11.7688 3.15997 12.25 3.15997 12.75H8.15997C8.15997 12.25 8.35997 11.7688 8.7131 11.4156L9.62872 10.5C10.6912 9.4375 11.285 8 11.285 6.5C11.285 3.39375 8.76622 0.875 5.65997 0.875ZM8.15997 6.5H6.28497V10.875H5.03497V6.5H3.15997V5.25H8.15997V6.5ZM3.15997 14H8.15997V15.25L6.28497 17.125H5.03497L3.15997 15.25V14Z"
          fill="#121212"
        />
      </svg>
    ),
    name: 'Design',
    description:
      'Customize form themes effortlessly with AI-chosen colors and fonts.',
    image: '/ai/menu2.webp'
  },
  {
    icons: (
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
        <path
          d="M6.12996 0.875L7.45496 2.2L3.31746 6.3375L0.579956 3.60312L1.90496 2.27812L3.31746 3.69063L6.12996 0.878125V0.875ZM3.31746 8.6875L1.90496 7.275L0.579956 8.6L3.31746 11.3375L7.45496 7.2L6.12996 5.875L3.31746 8.6875ZM3.31746 13.6875L1.90496 12.275L0.579956 13.6L3.31746 16.3375L7.45496 12.2L6.12996 10.875L3.31746 13.6875ZM8.07996 3.0625V4.9375H16.83V3.0625H8.07996ZM8.07996 9.9375H16.83V8.0625H8.07996V9.9375ZM8.07996 14.9375H16.83V13.0625H8.07996V14.9375Z"
          fill="#121212"
        />
      </svg>
    ),
    name: 'Analyze',
    description:
      'Gain actionable insights and improve engagement with AI analytics.',
    image: '/ai/menu3.webp'
  }
]

const Hero = () => {
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleNext = () => {
    setActiveTabIndex(prevIndex => {
      const currentIndex = (prevIndex + 1) % tabs.length
      return currentIndex
    })
  }

  const handlePrev = () => {
    setActiveTabIndex(prevIndex => (prevIndex - 1 + tabs.length) % tabs.length)
  }

  const scrollToTab = (index: number) => {
    const container = containerRef.current

    if (container) {
      const tabParent = container.children[0]
      const tabElement = tabParent.children[index] as HTMLElement
      if (tabElement) {
        container.scrollTo({
          left:
            tabElement.offsetLeft -
            (container.offsetWidth / 2 - tabElement.offsetWidth / 2),
          behavior: 'smooth'
        })
      }
    }
  }

  React.useEffect(() => {
    scrollToTab(activeTabIndex)
  }, [activeTabIndex])

  const activeTab = tabs[activeTabIndex]

  return (
    <div className="">
      <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-between pt-[80px]">
        <div className="pb-[40px] lg:pb-[80px] text-center lg:text-start">
          <h1 className="font-[700] text-[35px] md:text-[51px] leading-[40px] tracking-[-1.21px] md:leading-[65px]">
            Smarter forms, AI-driven.
          </h1>
          <p className="mt-[20px] text-[18px] md:text-[24px] font-[500] leading-[30px] max-w-[488px] text-[#050505]">
            Design forms faster and smarter - let our AI assist you every step
            of the way.
          </p>
          <button className="ctaButton mt-8">Get started for free</button>
        </div>
        <div className="lg:mt-16 xl:px-14">
          <Image
            src={`/ai/Hero.svg`}
            alt="Hero"
            width={226}
            height={215}
            className="flex justify-end items-end md:h-full"
          />
        </div>
      </div>

      <div className="w-full overflow-x-auto no-scrollbar" ref={containerRef}>
        <div className="-mt-3 flex md:grid md:grid-cols-3 gap-4 xl:gap-6 w-[800px] md:w-full overflow-x-auto">
          {tabs.map((tab, i) => (
            <div
              key={i}
              id={`tab_${i}`}
              className={`p-4 rounded-lg cursor-pointer border-[2px] border-[#64748B14] transition-all ${
                activeTabIndex === i
                  ? 'bg-white'
                  : 'bg-slate-50 hover:bg-slate-100/70'
              }`}
              onMouseEnter={() => setActiveTabIndex(i)}
            >
              <div className="w-full flex items-center lg:items-start lg:justify-center md:pt-2 gap-3 lg:flex-col">
                <div className="text-xl font-bold">{tab.icons}</div>
                <p className="text-xl font-bold">{tab.name}</p>
              </div>
              <p className="mt-2 text-slate-700 leading-tight">
                {tab.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 border-2 border-[#f6f5f4] lg:mt-16 w-full relative">
        <div className="relative w-full">
          <Image
            src={activeTab.image}
            alt={activeTab.name}
            width={500}
            height={638}
            className="object-cover w-full max-h-[638px] h-full"
          />

          <div
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onClick={handlePrev}
            className="absolute top-[0%] left-0 bg-transparent cursor-pointer w-1/2 h-full "
          >
            {open && (
              <button
                type="button"
                className="absolute left-4 top-[50%] transform -translate-y-1/2 p-2 bg-[#f6f5f4] rounded-full hover:bg-white"
              >
                <ChevronLeft size={32} />
              </button>
            )}
          </div>

          <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={handleNext}
            className="absolute top-[0%] right-0 bg-transparent cursor-pointer w-1/2 h-full "
          >
            {hover && (
              <button className="absolute right-4 top-[50%] transform -translate-y-1/2 p-2 bg-[#f6f5f4] rounded-full hover:bg-white">
                <ChevronRight size={32} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-between w-full bg-teal-50 px-[28px] md:px-[40px] pt-[28px] rounded-lg mt-8 md:mt-0">
        <div className="">
          <div className="flex lg:justify-start lg:text-start md:justify-center items-center gap-2 mb-2 text-start md:text-center">
            <div className="md:w-[26px] md:h-[26px] w-[16px] h-[16px]">
              <svg
                className="sparkles"
                viewBox="0 0 24 23"
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  fill: 'var(--color-icon)',
                  flexShrink: 0,
                  color: 'var(--color-icon)'
                }}
              >
                <g>
                  <path
                    d="M10.4563 5.72844C10.9113 5.57094 10.9113 5.29094 10.4563 5.11594L7.98881 4.17094C7.55131 4.01344 7.04381 3.50594 6.86881 3.05094L5.92381 0.583437C5.76631 0.128438 5.48631 0.128438 5.31131 0.583437L4.36631 3.05094C4.20881 3.48844 3.70131 3.99594 3.24631 4.17094L0.778811 5.11594C0.323811 5.27344 0.323811 5.55344 0.778811 5.72844L3.24631 6.67344C3.68381 6.83094 4.19131 7.33844 4.36631 7.79344L5.31131 10.2609C5.46881 10.7159 5.74881 10.7159 5.92381 10.2609L6.86881 7.79344C7.02631 7.35594 7.53381 6.84844 7.98881 6.67344L10.4563 5.72844Z"
                    fill="currentColor"
                  />
                  <path
                    d="M22.4788 14.3734C23.8788 14.0234 23.8788 13.4459 22.4788 13.0959L19.3813 12.3259C17.9813 11.9759 16.5463 10.5409 16.1963 9.14091L15.4263 6.04341C15.0763 4.64341 14.4988 4.64341 14.1488 6.04341L13.3788 9.14091C13.0288 10.5409 11.5938 11.9759 10.1938 12.3259L7.09631 13.0959C5.69631 13.4459 5.69631 14.0234 7.09631 14.3734L10.1938 15.1434C11.5938 15.4934 13.0288 16.9284 13.3788 18.3284L14.1488 21.4259C14.4988 22.8259 15.0763 22.8259 15.4263 21.4259L16.1963 18.3284C16.5463 16.9284 17.9813 15.4934 19.3813 15.1434L22.4788 14.3734Z"
                    fill="currentColor"
                  />
                </g>
              </svg>
            </div>
            <h2 className="font-bold text-xl md:text-2xl">
              HeyForm AI is FREE get started
            </h2>
          </div>
          <p className="max-w-[380px] text-start md:text-center lg:text-start">
            Sign up today and enjoy a 7-day free trial to explore all premium
            features. No commitments, just smart, intuitive form-building at
            your fingertips.
          </p>
          <div className="flex lg:justify-start justify-start md:justify-center">
            <button className="text-[#FFFFFF] text-[16px] leading-[23px] tracking-[0.001] font-[600] rounded-[5px] text-center  px-[14px] py-[8px] hover:bg-[#121212]/80  bg-[#121212] mt-6 flex items-center justify-center gap-1">
              Get started <ArrowRight size={20} />
            </button>
          </div>
        </div>

        <div className="mt-8 lg:mt-0">
          <Image
            src={`/ai/notion.webp`}
            alt={`ask a notion AI`}
            width={380}
            height={218}
            className="object-contain"
          />
        </div>
      </div>
    </div>
  )
}

export default Hero
